import React from 'react';
import { SectionType } from './DataModal.interface';
import 'primeicons/primeicons.css';

// ── NDC cell: show first 3 + static "+N more" label ─────────────────────────
// Option B: matching NDCs are always bumped to visible slots.
const NDC_VISIBLE_COUNT = 3;

const ndcMoreStyle: React.CSSProperties = {
  color: 'var(--gray-500, #6B7280)',
  whiteSpace: 'nowrap',
  fontWeight: 500,
  marginLeft: '0.25rem',
};

interface NdcCellRendererProps {
  ndcs: string[];
  searchTerm?: string;
}

const NdcCellRenderer: React.FC<NdcCellRendererProps> = ({ ndcs, searchTerm }) => {
  const term = searchTerm?.trim() ?? '';
  const lowerTerm = term.toLowerCase();

  // Sort matching NDCs to the front so they are never hidden in "+N more"
  const sorted = lowerTerm
    ? [
        ...ndcs.filter(n => n.toLowerCase().includes(lowerTerm)),
        ...ndcs.filter(n => !n.toLowerCase().includes(lowerTerm)),
      ]
    : ndcs;

  const visible = sorted.slice(0, NDC_VISIBLE_COUNT);
  const hiddenCount = sorted.length - NDC_VISIBLE_COUNT;

  const children: React.ReactNode[] = [];
  visible.forEach((ndc, i) => {
    if (i > 0) children.push(', ');
    children.push(
      React.createElement('span', { key: i }, term ? highlightText(ndc, term) : ndc)
    );
  });

  if (hiddenCount > 0) {
    children.push(
      React.createElement('span', { style: ndcMoreStyle, key: 'more' }, ` +${hiddenCount} more`)
    );
  }

  return React.createElement('span', null, ...children);
};

// ── Highlight helper: wraps the matched substring in a <mark> ────────────────
const highlightText = (text: string, term: string): React.ReactNode => {
  if (!term) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return React.createElement(
    React.Fragment,
    null,
    text.slice(0, idx),
    React.createElement('mark', { className: 'ce-concept-match' }, text.slice(idx, idx + term.length)),
    text.slice(idx + term.length)
  );
};

// ── Concept cell: show first 2 + static "+N more" label ──────────────────────
// Option B: matching concepts are always bumped to visible slots.
const CONCEPT_VISIBLE_COUNT = 2;

interface ConceptCellRendererProps {
  concepts: string[];
  searchTerm?: string;
}

const ConceptCellRenderer: React.FC<ConceptCellRendererProps> = ({ concepts, searchTerm }) => {
  const term = searchTerm?.trim() ?? '';
  const lowerTerm = term.toLowerCase();

  // Sort matching concepts to the front so they are never hidden in "+N more"
  const sorted = lowerTerm
    ? [
        ...concepts.filter(c => c.toLowerCase().includes(lowerTerm)),
        ...concepts.filter(c => !c.toLowerCase().includes(lowerTerm)),
      ]
    : concepts;

  const visible = sorted.slice(0, CONCEPT_VISIBLE_COUNT);
  const hiddenCount = sorted.length - CONCEPT_VISIBLE_COUNT;

  const children: React.ReactNode[] = [];
  visible.forEach((concept, i) => {
    if (i > 0) children.push(', ');
    children.push(
      React.createElement('span', { key: i }, term ? highlightText(concept, term) : concept)
    );
  });

  if (hiddenCount > 0) {
    children.push(
      React.createElement('span', { style: ndcMoreStyle, key: 'more' }, ` +${hiddenCount} more`)
    );
  }

  return React.createElement('span', null, ...children);
};

export interface ColumnConfig {
  field: string;
  header: string;
  width?: string;
  render?: (value: unknown, row: Record<string, unknown>, filterValue?: string) => React.ReactNode;
  dataType?: 'string' | 'date' | 'number';
  filterable?: boolean;
}

/**
 * Get column configuration for a specific section type
 */
export const getColumnsBySectionType = (sectionType: SectionType): ColumnConfig[] => {
  switch (sectionType) {
    case 'contractExpirations':
      return [
        {
          field: 'contract_owner',
          header: 'CONTRACT OWNER',
          width: '18%',
        },
        {
          field: 'contract_number',
          header: 'CONTRACT NUMBER',
          width: '18%',
          // render: (value: string) => React.createElement('strong', null, value.split('-')[0]),
        },
        {
          field: 'agency',
          header: 'AGENCY',
          width: '18%',
          // render: (value: string) => React.createElement('strong', null, value.split('-')[1]),
        },
        {
          field: 'contract_type',
          header: 'CONTRACT TYPE',
          width: '18%',
          // render: (value: string) => React.createElement('strong', null, value.split('-')[2]),
        },
        {
          field: 'concept_name',
          header: 'CONCEPT NAME',
          width: '18%',
          render: (value: unknown, _row: Record<string, unknown>, filterValue?: string) => {
            const raw = typeof value === 'string' ? value.trim() : '';
            if (!raw) return React.createElement('span', null, '—');
            const concepts = raw.split(',').map((c) => c.trim()).filter(Boolean);
            return React.createElement(ConceptCellRenderer, { concepts, searchTerm: filterValue });
          },
        },
        {
          field: 'ndcs',
          header: 'NDCS',
          width: '18%',
          render: (value: unknown, _row: Record<string, unknown>, filterValue?: string) => {
            const ndcs = Array.isArray(value) ? (value as string[]) : [];
            if (ndcs.length === 0) return React.createElement('span', null, (value as string) ?? '');
            return React.createElement(NdcCellRenderer, { ndcs, searchTerm: filterValue });
          },
        },

        // {
        //   field: 'description',
        //   header: 'CONTRACT DESCRIPTION',
        //   width: '30%',
        // },
        {
          field: 'date',
          header: 'EXPIRATION DATE',
          width: '18%',
          dataType: 'date',
        },
      ];

    case 'andaUpdates':
      return [
        {
          field: 'title',
          header: 'ANDA',
          width: '12%',
          render: (value: unknown, row: Record<string, unknown>) => {
            // Title format: "{ANDA number} - {Concept name} - {dosage form}"
            // Only show the ANDA number part
            const stringValue = String(value || '');
            const parts = stringValue.split(' - ');
            const andaNumber = parts[0];
            const color = (row?.color as string) || '#374151';

            return React.createElement('strong', { style: { color } }, andaNumber);
          },
        },
        {
          field: 'description',
          header: 'DESCRIPTION',
          width: '18%',
        },
        {
          field: 'marketing_status',
          header: 'MARKETING STATUS',
          width: '12%',
          render: (_value: unknown, row: Record<string, unknown>) => {
            return React.createElement('span', null, (row.marketing_status as string) || '-');
          },
        },
        {
          field: 'last_submission_date',
          header: 'LAST SUBMISSION DATE',
          width: '14%',
          dataType: 'date',
          render: (_value: unknown, row: Record<string, unknown>) => {
            if (!row.last_submission_date) return React.createElement('span', null, '-');
            const date = new Date(row.last_submission_date as string);
            const formatted = date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: '2-digit',
            });
            return React.createElement('span', null, formatted);
          },
        },
        {
          field: 'last_submission_type',
          header: 'LAST SUBMISSION TYPE',
          width: '12%',
          render: (_value: unknown, row: Record<string, unknown>) => {
            return React.createElement('span', null, (row.last_submission_type as string) || '-');
          },
        },
      ];

    case 'preSolicitationAlerts':
      return [
        {
          field: 'title',
          header: 'TITLE',
          width: '22%',
          render: (value: unknown) => React.createElement('strong', null, String(value || '')),
        },
        {
          field: 'description',
          header: 'AGENCY',
          width: '18%',
        },
        {
          field: 'posted_date',
          header: 'POSTED DATE',
          width: '12%',
          dataType: 'date',
          render: (_value: unknown, row: Record<string, unknown>) => {
            if (!row.posted_date) return React.createElement('span', null, '-');
            const date = new Date(row.posted_date as string);
            const formatted = date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
            return React.createElement('span', null, formatted);
          },
        },
        // {
        //   field: 'status',
        //   header: 'STATUS',
        //   width: '12%',
        //   render: (_value: any, row: any) => {
        //     const status = row.status || '-';
        //     const formattedStatus = status
        //       .replace(/_/g, ' ')
        //       .replace(/\b\w/g, (l: string) => l.toUpperCase());
        //     return React.createElement('span', null, formattedStatus);
        //   },
        // },
        {
          field: 'date',
          header: 'DUE DATE',
          width: '12%',
        },
        {
          field: 'url',
          header: '',
          width: '5%',
          filterable: false,
          render: (_value: unknown, row: Record<string, unknown>) => {
            if (!row.url) {
              return React.createElement('span', null, '-');
            }
            const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              window.open(row.url as string, '_blank', 'noopener,noreferrer');
            };
            return React.createElement(
              'button',
              {
                className: 'table-link-icon-btn',
                onClick: handleClick,
                'aria-label': 'Open link in new tab',
                title: 'Open link in new tab',
                style: {
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                } as React.CSSProperties,
              },
              React.createElement('i', { className: 'pi pi-external-link' })
            );
          },
        },
      ];

    case 'lossOfExclusivity':
      return [
        {
          field: 'generic_name',
          header: 'GENERIC NAME',
          width: '13%',
          render: (_value: unknown, row: Record<string, unknown>) => {
            // Use the separate generic_name field from the response
            const genericName = (row.generic_name as string) || '-';
            return React.createElement('strong', null, genericName);
          },
        },
        {
          field: 'brand_name',
          header: 'BRAND NAME',
          width: '10%',
          render: (_value: unknown, row: Record<string, unknown>) => {
            // Use the separate brand_name field from the response
            return React.createElement('span', null, (row.brand_name as string) || '-');
          },
        },
        {
          field: 'brand_company',
          header: 'BRAND COMPANY',
          width: '12%',
          render: (_value: unknown, row: Record<string, unknown>) => {
            // Use the separate brand_company field from the response
            return React.createElement('span', null, (row.brand_company as string) || '-');
          },
        },
        {
          field: 'description',
          header: 'DESCRIPTION',
          width: '12%',
        },
        {
          field: 'date',
          header: 'LOE DATE',
          width: '10%',
          dataType: 'date',
        },
        {
          // Unique field name to prevent filter state collision
          field: 'tags_launchDate',
          header: 'LAUNCH DATE',
          width: '10%',
          dataType: 'date',
          render: (_value: unknown, row: Record<string, unknown>) => {
            interface TagWithLaunchDate {
              earliest_possible_launch_date?: string;
            }
            const tags = row.tags as TagWithLaunchDate[] | undefined;
            const launchDateTag = tags?.find((tag) => tag.earliest_possible_launch_date);
            return React.createElement(
              'span',
              null,
              launchDateTag?.earliest_possible_launch_date
                ? launchDateTag.earliest_possible_launch_date.replace(/^Launch:\s*/, '')
                : '-'
            );
          },
        },
        {
          // Unique field name to prevent filter state collision
          field: 'tags_launchProbability',
          header: 'LAUNCH PROBABILITY',
          width: '10%',
          render: (_value: unknown, row: Record<string, unknown>) => {
            interface TagWithProbability {
              earliest_launch_probability?: string;
            }
            const tags = row.tags as TagWithProbability[] | undefined;
            const launchDateTag = tags?.find((tag) => tag.earliest_launch_probability);
            return React.createElement(
              'span',
              null,
              launchDateTag?.earliest_launch_probability?.split(' ')[0] || '-'
            );
          },
        },
        {
          field: 'first_anda_filers',
          header: 'First ANDA Filers',
          width: '11%',
          render: (_value: unknown, row: Record<string, unknown>) => {
            const value = (row.first_anda_filers as string) || '';
            if (!value) return React.createElement('span', null, '-');
            const maxLength = 30;
            if (value.length > maxLength) {
              const truncated = value.substring(0, maxLength) + '...';
              return React.createElement('span', { title: value }, truncated);
            }
            return React.createElement('span', null, value);
          },
        },
        {
          field: 'other_anda_filers',
          header: 'Other ANDA Filers',
          width: '11%',
          render: (_value: unknown, row: Record<string, unknown>) => {
            const value = (row.other_anda_filers as string) || '';
            if (!value) return React.createElement('span', null, '-');
            const maxLength = 30;
            if (value.length > maxLength) {
              const truncated = value.substring(0, maxLength) + '...';
              return React.createElement('span', { title: value }, truncated);
            }
            return React.createElement('span', null, value);
          },
        },
      ];

    // case 'marketIntelligence':
    //   return [
    //     {
    //       field: 'title',
    //       header: 'INSIGHT',
    //       width: '25%',
    //       render: (value: string) => React.createElement('strong', null, value),
    //     },
    //     {
    //       field: 'description',
    //       header: 'DETAILS',
    //       width: '40%',
    //     },
    //     {
    //       field: 'tags',
    //       header: 'CATEGORY',
    //       width: '15%',
    //       render: (_value: any, row: any) => {
    //         const categoryTag = row.tags?.find((tag: any) => tag.category);
    //         return React.createElement('span', null, categoryTag?.category || '-');
    //       },
    //     },
    //     {
    //       field: 'date',
    //       header: 'DATE',
    //       width: '20%',
    //     },
    //   ];

    default:
      return [];
  }
};
